import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { PrismModule, SettingsSubModule } from '@prism/prism-shared';
import { scaleLinear, ScaleLinear, select } from 'd3';
import { filter, map, Subscription, tap } from 'rxjs';
import { CoreSelectors } from '../../state/core';

interface Star {
  x: number;
  y: number;
  size: number;
  vx: number;
  color: string;
}

interface StarlinkTrain {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  directionX: number;
  directionY: number;
  count: number;
  spacing: number;
  duration: number;
  opacity: number;
  size: number;
  spacingJitter: number;
}

interface StarlinkDot {
  x: number;
  y: number;
  color: string;
}

interface Point {
  x: number;
  y: number;
}

interface StarlinkNetworkNode extends Point {
  trailX: number;
  trailY: number;
  travel: number;
  normalOffset: number;
  directionX: number;
  directionY: number;
  normalX: number;
  normalY: number;
  centerX: number;
  centerY: number;
  halfTravel: number;
  velocity: number;
  color: string;
}

type ThemeName = 'christmas' | 'fourthOfJuly' | 'normal';
type ThemeSelection = ThemeName | 'auto';
type ThemeMap = {
  [K in ThemeName]: () => string;
};

interface HolidayThemeWindow {
  themeName: ThemeName;
  monthIndex: number;
  day: number;
}

const BACKGROUND_CONFIG = {
  defaults: {
    aspectWidth: 5,
    aspectHeight: 3,
    backgroundColor: 'var(--color-1)',
    averageSupernovaInterval: 10000,
    holidayThemeDayThreshold: 7,
    showStarlinkNetwork: false,
    showOnlyStarlinkNetwork: false,
    starlinkNetworkDensityMultiplier: 1,
    starlinkNetworkBrightnessMultiplier: 1,
    starlinkNetworkColor5Percent: 10,
  },
  theme: {
    christmasGreenChance: 0.7,
    christmasGreen: '#2d9964',
    christmasRed: '#f85b5b',
    fourthOfJulyRedChance: 0.34,
    fourthOfJulyWhiteChance: 0.35,
    fourthOfJulyRed: '#ff4d5f',
    fourthOfJulyWhite: '#f7fbff',
    fourthOfJulyBlue: '#4c8dff',
    normal: 'white',
    color5: 'var(--color-5)',
  },
  holidayThemes: [
    { themeName: 'fourthOfJuly', monthIndex: 6, day: 4 },
    { themeName: 'christmas', monthIndex: 11, day: 25 },
  ] as HolidayThemeWindow[],
  math: {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    six: 6,
    twelve: 12,
    twentyFour: 24,
    sixty: 60,
    oneHundred: 100,
    millisecondsPerSecond: 1000,
    fullCircle: Math.PI * 2,
    halfCircle: Math.PI,
  },
  canvas: {
    positionZero: '0px',
    backgroundZIndex: '-1',
  },
  stars: {
    count: 200,
    sizeMin: 0.5,
    sizeRange: 1.5,
    velocityMin: 0.1,
    velocityRange: 0.3,
    opacity: 0.8,
  },
  network: {
    dotBaseSize: 1.25,
    dotSizeMultiplier: 2,
    orbitalPlaneAngles: [0, Math.PI / 6, Math.PI / 3, Math.PI / 2, 2 * Math.PI / 3, 5 * Math.PI / 6],
    baseOpacity: 0.16,
    trailOpacity: 0.34,
    minDensityMultiplier: 0.1,
    dotSpacing: 165,
    trackSpacing: 360,
    margin: 180,
    velocityMultiplier: 0.55,
    trailLength: 7,
  },
  supernova: {
    intervalVariation: 0.5,
    particleCount: 12,
    angleJitter: 0.5,
    speedMin: 30,
    speedRange: 40,
    particleSizeMin: 1,
    particleSizeRange: 2,
    hueMin: 20,
    hueRange: 60,
    saturationPercent: 100,
    lightnessPercent: 70,
    opacity: 0.9,
    durationMin: 800,
    durationRange: 400,
    cleanupDelay: 1500,
    finalRadius: 0,
    finalOpacity: 0,
  },
  satellite: {
    scheduleDelayMin: 750,
    scheduleDelayMax: 2500,
    rocketChance: 0.08,
    color5Chance: 0.1,
    fadeInDuration: 900,
    fadeOutDuration: 1200,
    trainDotCountMin: 6,
    trainDotCountMax: 24,
    trainDotLongBiasExponent: 2.8,
    trainDotShortfallChance: 0.25,
    trainDotShortfallMax: 2,
    trainDotAbsoluteMin: 4,
    easterEggChance: 0.002,
    easterEggDotCount: 120,
    spacingMin: 12,
    spacingMax: 28,
    spacingFarScale: 0.65,
    spacingNearScale: 1.15,
    margin: 80,
    opacityMin: 0.18,
    opacityMax: 0.52,
    opacityFarScale: 0.45,
    opacityNearScale: 1,
    spacingJitterMin: 0.03,
    spacingJitterMax: 0.18,
    defaultStarAverageSize: 1.25,
    dotCloseScaleMin: 1.32,
    dotCloseScaleMax: 1.68,
    dotVariedScaleMin: 0.8,
    dotVariedScaleMax: 1.85,
    dotDepthFarScale: 0.55,
    dotDepthNearScale: 1,
    speedSizeMin: 0.5,
    speedSizeMax: 2,
    speedPerFrameMin: 0.1,
    speedPerFrameMax: 0.4,
    speedMultiplier: 2,
  },
  rocket: {
    arcDirectionChance: 0.5,
    arcDirectionNegative: -1,
    arcDirectionPositive: 1,
    arcHeightViewportMin: 0.12,
    arcHeightViewportMax: 0.32,
    rocketSizeMultiplier: 2.4,
    rocketOpacityBoost: 0.2,
    rocketOpacityMax: 0.75,
    fadeInDuration: 900,
    fadeOutDuration: 700,
    cleanupDelay: 16000,
    releaseDistanceMin: 1.1,
    releaseDistanceMax: 2.2,
    releaseFadeInDuration: 300,
    releaseMoveDuration: 1400,
    releaseFadeOutDuration: 12000,
  },
  curve: {
    lengthSamples: 24,
  },
} as const;

@Component({
  selector: 'app-background',
  templateUrl: './background.component.html',
  styleUrls: ['./background.component.scss'],

  //   imports: [CommonModule], // Add other modules if your component uses them0
  //   standalone: true,
})
export class BackgroundComponent implements OnInit, AfterViewInit, OnDestroy {
  // Separate inputs for aspect ratio
  @Input() aspectWidth: number = BACKGROUND_CONFIG.defaults.aspectWidth;  // Default X value
  @Input() aspectHeight: number = BACKGROUND_CONFIG.defaults.aspectHeight; // Default Y value
  @Input() backgroundColor: string = BACKGROUND_CONFIG.defaults.backgroundColor; // Updated to black for starry sky
  @Input() averageSupernovaInterval: number = BACKGROUND_CONFIG.defaults.averageSupernovaInterval; // Average interval in ms between supernovas
    @Input() showStarlinkNetwork: boolean = BACKGROUND_CONFIG.defaults.showStarlinkNetwork;
    @Input() showOnlyStarlinkNetwork: boolean = BACKGROUND_CONFIG.defaults.showOnlyStarlinkNetwork;
    @Input() starlinkNetworkDensityMultiplier: number = BACKGROUND_CONFIG.defaults.starlinkNetworkDensityMultiplier;
    @Input() starlinkNetworkBrightnessMultiplier: number = BACKGROUND_CONFIG.defaults.starlinkNetworkBrightnessMultiplier;
    @Input() starlinkNetworkColor5Percent: number = BACKGROUND_CONFIG.defaults.starlinkNetworkColor5Percent;
    @Input() themeName: ThemeSelection = 'auto';
    @Input() holidayThemeDayThreshold: number = BACKGROUND_CONFIG.defaults.holidayThemeDayThreshold;

  themes: ThemeMap = {
    christmas: () => Math.random() < BACKGROUND_CONFIG.theme.christmasGreenChance ? BACKGROUND_CONFIG.theme.christmasGreen : BACKGROUND_CONFIG.theme.christmasRed,
    fourthOfJuly: () => {
      const roll = Math.random();
      if (roll < BACKGROUND_CONFIG.theme.fourthOfJulyRedChance) return BACKGROUND_CONFIG.theme.fourthOfJulyRed;
      if (roll < BACKGROUND_CONFIG.theme.fourthOfJulyRedChance + BACKGROUND_CONFIG.theme.fourthOfJulyWhiteChance) return BACKGROUND_CONFIG.theme.fourthOfJulyWhite;
      return BACKGROUND_CONFIG.theme.fourthOfJulyBlue;
    },
    normal: () => BACKGROUND_CONFIG.theme.normal,
  }
  selectedTheme!: () => string;

  public svg: any;
  public xScale!: ScaleLinear<number, number>;
  public yScale!: ScaleLinear<number, number>;

  public canvasWidth: number = BACKGROUND_CONFIG.math.zero;
  public canvasHeight: number = BACKGROUND_CONFIG.math.zero;

  private stars: Star[] = [];
  private starlinkNetworkNodes: StarlinkNetworkNode[] = [];
  private starSelection: any;
  private starlinkNetworkSelection: any;
  private starlinkNetworkTrailSelection: any;
  private animationId: number | null = null;
  private rocketAnimationId: number | null = null;
  private supernovaTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private starlinkTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private backgroundSettingsSubscription: Subscription | null = null;
  private initialized = false;
  private destroyed = false;

  constructor(
    private elementRef: ElementRef,
    private store: Store,
  ) { }

  ngOnInit() {
    this.selectedTheme = this.themes[this.resolveThemeName()] || this.themes.normal
    // Defer setup to after view init for accurate dimensions
  }

  private resolveThemeName(date: Date = new Date()): ThemeName {
    if (this.themeName !== 'auto') return this.themeName;

    const thresholdDays = Math.max(BACKGROUND_CONFIG.math.zero, this.holidayThemeDayThreshold);
    const activeHoliday = BACKGROUND_CONFIG.holidayThemes.find(holiday => this.isWithinHolidayThemeWindow(date, holiday, thresholdDays));

    return activeHoliday?.themeName ?? 'normal';
  }

  private isWithinHolidayThemeWindow(date: Date, holiday: HolidayThemeWindow, thresholdDays: number): boolean {
    const currentDay = this.toUtcDay(date.getFullYear(), date.getMonth(), date.getDate());
    const holidayDays = [
      this.toUtcDay(date.getFullYear() - BACKGROUND_CONFIG.math.one, holiday.monthIndex, holiday.day),
      this.toUtcDay(date.getFullYear(), holiday.monthIndex, holiday.day),
      this.toUtcDay(date.getFullYear() + BACKGROUND_CONFIG.math.one, holiday.monthIndex, holiday.day),
    ];

    return holidayDays.some(holidayDay => Math.abs(currentDay - holidayDay) <= thresholdDays);
  }

  private toUtcDay(year: number, monthIndex: number, day: number): number {
    return Date.UTC(year, monthIndex, day) / (
      BACKGROUND_CONFIG.math.twentyFour *
      BACKGROUND_CONFIG.math.sixty *
      BACKGROUND_CONFIG.math.sixty *
      BACKGROUND_CONFIG.math.millisecondsPerSecond
    );
  }

  ngAfterViewInit() {
    this.backgroundSettingsSubscription = this.store.select(CoreSelectors.getCurrentUser).pipe(
      filter(user => !!user),
      map(user => user.abilities[PrismModule.Settings][SettingsSubModule.Display].disableBackground),
      tap(disableBackground => {
        if (!disableBackground && !this.initialized) {
          this.initialized = true;
          this.setupCanvas();

          if (!this.svg) return;

          if (this.showStarlinkNetwork) {
            this.generateStarlinkGridLayer();
          }

          if (!this.showOnlyStarlinkNetwork) {
            this.generateStars();
            this.scheduleNextSupernova();
            this.launchStarlinkTrain();
            this.launchRocketDeployment();
            this.scheduleNextStarlinkTrain();
          }

          this.startAnimation();
        }
      })
    ).subscribe()
  }

  ngOnDestroy() {
    this.destroyed = true;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    if (this.rocketAnimationId) {
      cancelAnimationFrame(this.rocketAnimationId);
    }

    if (this.supernovaTimeoutId) {
      clearTimeout(this.supernovaTimeoutId);
    }

    if (this.starlinkTimeoutId) {
      clearTimeout(this.starlinkTimeoutId);
    }

    this.backgroundSettingsSubscription?.unsubscribe();
  }

  public setupCanvas() {
    // Use window dimensions for full viewport coverage
    this.canvasWidth = window.innerWidth;
    this.canvasHeight = window.innerHeight;

    // Set container dimensions with JS instead of vh/vw
    const container = this.elementRef.nativeElement.querySelector('.container');
    if (container) {
      container.style.width = `${this.canvasWidth}px`;
      container.style.height = `${this.canvasHeight}px`;
    }

    const flowCanvas = this.elementRef.nativeElement.querySelector('#flow-canvas');
    if (!flowCanvas) return;

    // Set up SVG with full viewport dimensions
    this.svg = select(flowCanvas)
      .append('svg')
      .attr('width', this.canvasWidth)
      .attr('height', this.canvasHeight)
      .style('position', 'absolute')
      .style('left', BACKGROUND_CONFIG.canvas.positionZero)
      .style('top', BACKGROUND_CONFIG.canvas.positionZero)
      .style('background-color', this.backgroundColor)
      .style('z-index', BACKGROUND_CONFIG.canvas.backgroundZIndex); // Ensure background is behind other content

    // Configure scales for full canvas
    this.xScale = scaleLinear().domain([BACKGROUND_CONFIG.math.zero, BACKGROUND_CONFIG.math.one]).range([BACKGROUND_CONFIG.math.zero, this.canvasWidth]);
    this.yScale = scaleLinear().domain([BACKGROUND_CONFIG.math.zero, BACKGROUND_CONFIG.math.one]).range([BACKGROUND_CONFIG.math.zero, this.canvasHeight]);
  }

  private generateStars() {
    const numStars = BACKGROUND_CONFIG.stars.count; // Number of stars for density
    this.stars = Array.from({ length: numStars }, () => ({
      x: Math.random() * this.canvasWidth,
      y: Math.random() * this.canvasHeight,
      size: Math.random() * BACKGROUND_CONFIG.stars.sizeRange + BACKGROUND_CONFIG.stars.sizeMin, // Varying small sizes
      vx: -(Math.random() * BACKGROUND_CONFIG.stars.velocityRange + BACKGROUND_CONFIG.stars.velocityMin), // Slow leftward velocity
      color: this.selectedTheme()
    }));

    const starGroup = this.svg.append('g').classed('stars', true);

    this.starSelection = starGroup.selectAll('circle')
      .data(this.stars)
      .enter()
      .append('circle')
      .attr('r', (d: Star) => d.size)
      .attr('fill', (d: Star) => d.color)
      .attr('cx', (d: Star) => d.x)
      .attr('cy', (d: Star) => d.y)
      .attr('opacity', BACKGROUND_CONFIG.stars.opacity); // Slight transparency for depth

    this.starSelection = starGroup.selectAll('circle');
  }

  private generateStarlinkGridLayer() {
    const dotSize = BACKGROUND_CONFIG.network.dotBaseSize * BACKGROUND_CONFIG.network.dotSizeMultiplier;
    const orbitalPlaneAngles = BACKGROUND_CONFIG.network.orbitalPlaneAngles;
    this.starlinkNetworkNodes = orbitalPlaneAngles.flatMap((angle, index) => (
      this.createStarlinkGridDots(angle, index % BACKGROUND_CONFIG.math.two === BACKGROUND_CONFIG.math.zero ? BACKGROUND_CONFIG.math.one : -BACKGROUND_CONFIG.math.one)
    ));
    this.applyStarlinkNetworkAccentColors();

    const gridGroup = this.svg.append('g')
      .classed('starlink-grid', true)
      .attr('opacity', BACKGROUND_CONFIG.network.baseOpacity * this.starlinkNetworkBrightnessMultiplier);

    this.starlinkNetworkTrailSelection = gridGroup.selectAll('line')
      .data(this.starlinkNetworkNodes)
      .enter()
      .append('line')
      .attr('x1', (d: StarlinkNetworkNode) => d.trailX)
      .attr('y1', (d: StarlinkNetworkNode) => d.trailY)
      .attr('x2', (d: StarlinkNetworkNode) => d.x)
      .attr('y2', (d: StarlinkNetworkNode) => d.y)
      .attr('stroke', (d: StarlinkNetworkNode) => d.color)
      .attr('stroke-width', dotSize)
      .attr('stroke-linecap', 'round')
      .attr('opacity', BACKGROUND_CONFIG.network.trailOpacity);

    this.starlinkNetworkSelection = gridGroup.selectAll('circle')
      .data(this.starlinkNetworkNodes)
      .enter()
      .append('circle')
      .attr('cx', (d: StarlinkNetworkNode) => d.x)
      .attr('cy', (d: StarlinkNetworkNode) => d.y)
      .attr('r', dotSize)
      .attr('fill', (d: StarlinkNetworkNode) => d.color);

    this.starlinkNetworkTrailSelection = gridGroup.selectAll('line');
    this.starlinkNetworkSelection = gridGroup.selectAll('circle');
  }

  private createStarlinkGridDots(angle: number, directionSign: number) {
    const dots: StarlinkNetworkNode[] = [];
    const densityScale = BACKGROUND_CONFIG.math.one / Math.sqrt(Math.max(this.starlinkNetworkDensityMultiplier, BACKGROUND_CONFIG.network.minDensityMultiplier));
    const dotSpacing = BACKGROUND_CONFIG.network.dotSpacing * densityScale;
    const trackSpacing = BACKGROUND_CONFIG.network.trackSpacing * densityScale;
    const margin = BACKGROUND_CONFIG.network.margin;
    const direction = {
      x: Math.cos(angle),
      y: Math.sin(angle),
    };
    const normal = {
      x: -direction.y,
      y: direction.x,
    };
    const center = {
      x: this.canvasWidth / BACKGROUND_CONFIG.math.two,
      y: this.canvasHeight / BACKGROUND_CONFIG.math.two,
    };
    const halfTravel = Math.sqrt(this.canvasWidth ** BACKGROUND_CONFIG.math.two + this.canvasHeight ** BACKGROUND_CONFIG.math.two) / BACKGROUND_CONFIG.math.two + margin;
    const maxOffset = this.getMaxPerpendicularOffset(normal.x, normal.y) + margin;
    const velocity = (this.getStarlinkSpeedPerSecond(BACKGROUND_CONFIG.network.dotBaseSize) / BACKGROUND_CONFIG.math.sixty) * BACKGROUND_CONFIG.network.velocityMultiplier * directionSign;
    let rowIndex = BACKGROUND_CONFIG.math.zero;

    for (let normalOffset = -maxOffset; normalOffset <= maxOffset; normalOffset += trackSpacing) {
      const rowOffset = rowIndex % BACKGROUND_CONFIG.math.two === BACKGROUND_CONFIG.math.zero ? BACKGROUND_CONFIG.math.zero : dotSpacing / BACKGROUND_CONFIG.math.two;

      for (let travel = -halfTravel + rowOffset; travel <= halfTravel; travel += dotSpacing) {
        dots.push({
          x: center.x + normal.x * normalOffset + direction.x * travel,
          y: center.y + normal.y * normalOffset + direction.y * travel,
          trailX: center.x + normal.x * normalOffset + direction.x * (travel - BACKGROUND_CONFIG.network.trailLength * directionSign),
          trailY: center.y + normal.y * normalOffset + direction.y * (travel - BACKGROUND_CONFIG.network.trailLength * directionSign),
          travel,
          normalOffset,
          directionX: direction.x,
          directionY: direction.y,
          normalX: normal.x,
          normalY: normal.y,
          centerX: center.x,
          centerY: center.y,
          halfTravel,
          velocity,
          color: this.selectedTheme(),
        });
      }

      rowIndex += BACKGROUND_CONFIG.math.one;
    }

    return dots;
  }

  private applyStarlinkNetworkAccentColors() {
    const color5Count = Math.round(this.starlinkNetworkNodes.length * this.clamp(this.starlinkNetworkColor5Percent, BACKGROUND_CONFIG.math.zero, BACKGROUND_CONFIG.math.oneHundred) / BACKGROUND_CONFIG.math.oneHundred);

    if (!color5Count) return;

    const color5Interval = this.starlinkNetworkNodes.length / color5Count;

    this.starlinkNetworkNodes.forEach((node, index) => {
      node.color = Math.floor(index / color5Interval) !== Math.floor((index - BACKGROUND_CONFIG.math.one) / color5Interval)
        ? this.selectedTheme()
        : node.color;
    });
  }

  private addNewStar() {
    const newStar: Star = {
      x: Math.random() * this.canvasWidth,
      y: Math.random() * this.canvasHeight,
      size: Math.random() * BACKGROUND_CONFIG.stars.sizeRange + BACKGROUND_CONFIG.stars.sizeMin,
      vx: -(Math.random() * BACKGROUND_CONFIG.stars.velocityRange + BACKGROUND_CONFIG.stars.velocityMin),
      color: this.selectedTheme()
    };

    this.stars.push(newStar);

    const starGroup = this.svg.select('.stars');
    starGroup.append('circle')
      .datum(newStar)
      .attr('r', newStar.size)
      .attr('fill', (d: Star) => d.color)
      .attr('cx', newStar.x)
      .attr('cy', newStar.y)
      .attr('opacity', BACKGROUND_CONFIG.stars.opacity);

    this.starSelection = starGroup.selectAll('circle');
  }

  private explodeRandomStar() {
    if (this.stars.length < BACKGROUND_CONFIG.math.one) return;

    const index = Math.floor(Math.random() * this.stars.length);
    const star = this.stars[index];
    const x = star.x;
    const y = star.y;

    // Remove the star from selection and array
    this.starSelection.filter((d: Star, i: number) => i === index).remove();
    this.stars.splice(index, BACKGROUND_CONFIG.math.one);

    // Create explosion particles
    const numParticles = BACKGROUND_CONFIG.supernova.particleCount;
    const explosionGroup = this.svg.append('g').classed('explosion', true);

    for (let i = BACKGROUND_CONFIG.math.zero; i < numParticles; i += BACKGROUND_CONFIG.math.one) {
      const angle = (i / numParticles) * BACKGROUND_CONFIG.math.fullCircle + (Math.random() - BACKGROUND_CONFIG.supernova.angleJitter);
      const speed = BACKGROUND_CONFIG.supernova.speedMin + Math.random() * BACKGROUND_CONFIG.supernova.speedRange;
      const particleSize = BACKGROUND_CONFIG.supernova.particleSizeMin + Math.random() * BACKGROUND_CONFIG.supernova.particleSizeRange;
      const particleColor = this.selectedTheme();

      const particle = explosionGroup.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', particleSize)
        .attr('fill', particleColor)
        .attr('opacity', BACKGROUND_CONFIG.supernova.opacity);

      // Animate outward
      const dx = Math.cos(angle) * speed;
      const dy = Math.sin(angle) * speed;
      const duration = BACKGROUND_CONFIG.supernova.durationMin + Math.random() * BACKGROUND_CONFIG.supernova.durationRange;

      particle.transition()
        .duration(duration)
        .attr('cx', x + dx)
        .attr('cy', y + dy)
        .attr('r', BACKGROUND_CONFIG.supernova.finalRadius)
        .attr('opacity', BACKGROUND_CONFIG.supernova.finalOpacity);
    }

    // Remove explosion group after animation
    setTimeout(() => {
      explosionGroup.remove();
    }, BACKGROUND_CONFIG.supernova.cleanupDelay);

    // Add a new star to replace
    this.addNewStar();
  }

  private scheduleNextSupernova() {
    const variation = BACKGROUND_CONFIG.supernova.intervalVariation;
    const delay = this.averageSupernovaInterval * (BACKGROUND_CONFIG.math.one - variation + Math.random() * (BACKGROUND_CONFIG.math.two * variation));
    this.supernovaTimeoutId = setTimeout(() => {
      if (this.destroyed) return;

      this.explodeRandomStar();
      this.scheduleNextSupernova();
    }, delay);
  }

  private scheduleNextStarlinkTrain() {
    const delay = this.randomBetween(BACKGROUND_CONFIG.satellite.scheduleDelayMin, BACKGROUND_CONFIG.satellite.scheduleDelayMax);

    this.starlinkTimeoutId = setTimeout(() => {
      if (this.destroyed) return;

      if (!this.rocketAnimationId && Math.random() < BACKGROUND_CONFIG.satellite.rocketChance) {
        this.launchRocketDeployment();
      } else {
        this.launchStarlinkTrain();
      }

      this.scheduleNextStarlinkTrain();
    }, delay);
  }

  private launchStarlinkTrain() {
    if (!this.svg) return;

    const train = this.createStarlinkTrain();
    const gaps = Array.from(
      { length: train.count - BACKGROUND_CONFIG.math.one },
      () => train.spacing + this.randomBetween(-train.spacingJitter, train.spacingJitter)
    );
    const totalLength = gaps.reduce((sum, gap) => sum + gap, BACKGROUND_CONFIG.math.zero);
    let offset = -totalLength / BACKGROUND_CONFIG.math.two;
    const dots: StarlinkDot[] = Array.from({ length: train.count }, (_, index) => {
      if (index > BACKGROUND_CONFIG.math.zero) {
        offset += gaps[index - BACKGROUND_CONFIG.math.one];
      }

      return {
        x: train.directionX * offset,
        y: train.directionY * offset,
        color: this.selectedTheme(),
      };
    });

    const trainGroup = this.svg.append('g')
      .classed('starlink-train', true)
      .attr('transform', `translate(${train.startX}, ${train.startY})`)
      .attr('opacity', BACKGROUND_CONFIG.math.zero);

    trainGroup.selectAll('circle')
      .data(dots)
      .enter()
      .append('circle')
      .attr('cx', (d: StarlinkDot) => d.x)
      .attr('cy', (d: StarlinkDot) => d.y)
      .attr('r', train.size)
      .attr('fill', (d: StarlinkDot) => d.color)
      .attr('opacity', train.opacity);

    trainGroup.transition()
      .duration(BACKGROUND_CONFIG.satellite.fadeInDuration)
      .attr('opacity', BACKGROUND_CONFIG.math.one)
      .transition()
      .duration(train.duration)
      .ease((t: number) => t)
      .attr('transform', `translate(${train.endX}, ${train.endY})`)
      .transition()
      .duration(BACKGROUND_CONFIG.satellite.fadeOutDuration)
      .attr('opacity', BACKGROUND_CONFIG.math.zero)
      .remove();
  }

  private launchRocketDeployment() {
    if (!this.svg) return;

    const train = this.createStarlinkTrain();
    const start: Point = { x: train.startX, y: train.startY };
    const end: Point = { x: train.endX, y: train.endY };
    const arcDirection = Math.random() < BACKGROUND_CONFIG.rocket.arcDirectionChance ? BACKGROUND_CONFIG.rocket.arcDirectionNegative : BACKGROUND_CONFIG.rocket.arcDirectionPositive;
    const arcHeight = this.randomBetween(this.canvasHeight * BACKGROUND_CONFIG.rocket.arcHeightViewportMin, this.canvasHeight * BACKGROUND_CONFIG.rocket.arcHeightViewportMax);
    const control: Point = {
      x: (start.x + end.x) / BACKGROUND_CONFIG.math.two + -train.directionY * arcHeight * arcDirection,
      y: (start.y + end.y) / BACKGROUND_CONFIG.math.two + train.directionX * arcHeight * arcDirection,
    };
    const pathLength = this.getQuadraticCurveLength(start, control, end);
    const duration = this.getStarlinkDuration(train.size, pathLength);
    const speedPerSecond = this.getStarlinkSpeedPerSecond(train.size);
    const rocketGroup = this.svg.append('g')
      .classed('starlink-rocket-deployment', true)
      .attr('opacity', BACKGROUND_CONFIG.math.zero);
    const satellitesGroup = rocketGroup.append('g').classed('rocket-satellites', true);
    const rocket = rocketGroup.append('circle')
      .attr('r', train.size * BACKGROUND_CONFIG.rocket.rocketSizeMultiplier)
      .attr('fill', this.selectedTheme())
      .attr('opacity', Math.min(train.opacity + BACKGROUND_CONFIG.rocket.rocketOpacityBoost, BACKGROUND_CONFIG.rocket.rocketOpacityMax));
    const gaps = Array.from(
      { length: train.count - BACKGROUND_CONFIG.math.one },
      () => train.spacing + this.randomBetween(-train.spacingJitter, train.spacingJitter)
    );
    const releaseIntervals = gaps.map(gap => (gap / speedPerSecond) * BACKGROUND_CONFIG.math.millisecondsPerSecond);
    const startedAt = performance.now();
    let releasedCount = BACKGROUND_CONFIG.math.zero;
    let nextReleaseAt = BACKGROUND_CONFIG.math.zero;

    rocketGroup.transition()
      .duration(BACKGROUND_CONFIG.rocket.fadeInDuration)
      .attr('opacity', BACKGROUND_CONFIG.math.one);

    const step = (now: number) => {
      if (this.destroyed) {
        rocketGroup.remove();
        this.rocketAnimationId = null;
        return;
      }

      const elapsed = now - startedAt;
      const progress = this.clamp(elapsed / duration, BACKGROUND_CONFIG.math.zero, BACKGROUND_CONFIG.math.one);
      const position = this.getQuadraticPoint(start, control, end, progress);
      const tangent = this.getQuadraticTangent(start, control, end, progress);
      const normal = this.getNormalizedVector(-tangent.y, tangent.x);

      rocket
        .attr('cx', position.x)
        .attr('cy', position.y);

      while (releasedCount < train.count && elapsed >= nextReleaseAt) {
        this.releaseRocketSatellite(satellitesGroup, position, normal, train, releasedCount);
        nextReleaseAt += releaseIntervals[releasedCount] ?? (train.spacing / speedPerSecond) * BACKGROUND_CONFIG.math.millisecondsPerSecond;
        releasedCount += BACKGROUND_CONFIG.math.one;
      }

      if (progress < BACKGROUND_CONFIG.math.one) {
        this.rocketAnimationId = requestAnimationFrame(step);
        return;
      }

      rocket.transition()
        .duration(BACKGROUND_CONFIG.rocket.fadeOutDuration)
        .attr('opacity', BACKGROUND_CONFIG.math.zero)
        .remove();

      setTimeout(() => rocketGroup.remove(), BACKGROUND_CONFIG.rocket.cleanupDelay);
      this.rocketAnimationId = null;
    };

    this.rocketAnimationId = requestAnimationFrame(step);
  }

  private releaseRocketSatellite(
    satellitesGroup: any,
    position: Point,
    normal: Point,
    train: StarlinkTrain,
    index: number
  ) {
    const releaseDistance = train.spacing * this.randomBetween(BACKGROUND_CONFIG.rocket.releaseDistanceMin, BACKGROUND_CONFIG.rocket.releaseDistanceMax);
    const direction = index % BACKGROUND_CONFIG.math.two === BACKGROUND_CONFIG.math.zero ? BACKGROUND_CONFIG.math.one : -BACKGROUND_CONFIG.math.one;
    const color = this.selectedTheme();
    const satellite = satellitesGroup.append('circle')
      .attr('cx', position.x)
      .attr('cy', position.y)
      .attr('r', train.size)
      .attr('fill', color)
      .attr('opacity', BACKGROUND_CONFIG.math.zero);

    satellite.transition()
      .duration(BACKGROUND_CONFIG.rocket.releaseFadeInDuration)
      .attr('opacity', train.opacity)
      .transition()
      .duration(BACKGROUND_CONFIG.rocket.releaseMoveDuration)
      .attr('cx', position.x + normal.x * releaseDistance * direction)
      .attr('cy', position.y + normal.y * releaseDistance * direction)
      .transition()
      .duration(BACKGROUND_CONFIG.rocket.releaseFadeOutDuration)
      .attr('opacity', BACKGROUND_CONFIG.math.zero)
      .remove();
  }

  private createStarlinkTrain(): StarlinkTrain {
    const angle = Math.random() * BACKGROUND_CONFIG.math.fullCircle;
    const directionX = Math.cos(angle);
    const directionY = Math.sin(angle);
    const perpendicularX = -directionY;
    const perpendicularY = directionX;
    const centerX = this.canvasWidth / BACKGROUND_CONFIG.math.two;
    const centerY = this.canvasHeight / BACKGROUND_CONFIG.math.two;
    const depth = Math.random();
    const count = this.getStarlinkDotCount();
    const spacing = this.randomBetween(BACKGROUND_CONFIG.satellite.spacingMin, BACKGROUND_CONFIG.satellite.spacingMax) * this.scaleByDepth(depth, BACKGROUND_CONFIG.satellite.spacingFarScale, BACKGROUND_CONFIG.satellite.spacingNearScale);
    const trainLength = (count - BACKGROUND_CONFIG.math.one) * spacing;
    const margin = trainLength / BACKGROUND_CONFIG.math.two + BACKGROUND_CONFIG.satellite.margin;
    const travelDistance = Math.sqrt(this.canvasWidth ** BACKGROUND_CONFIG.math.two + this.canvasHeight ** BACKGROUND_CONFIG.math.two) / BACKGROUND_CONFIG.math.two + margin;
    const maxOffset = this.getMaxPerpendicularOffset(perpendicularX, perpendicularY);
    const offset = this.randomBetween(-maxOffset, maxOffset);
    const baseX = centerX + perpendicularX * offset;
    const baseY = centerY + perpendicularY * offset;
    const size = this.getStarlinkDotSize(depth);

    return {
      startX: baseX - directionX * travelDistance,
      startY: baseY - directionY * travelDistance,
      endX: baseX + directionX * travelDistance,
      endY: baseY + directionY * travelDistance,
      directionX,
      directionY,
      count,
      spacing,
      duration: this.getStarlinkDuration(size, travelDistance * BACKGROUND_CONFIG.math.two),
      opacity: this.randomBetween(BACKGROUND_CONFIG.satellite.opacityMin, BACKGROUND_CONFIG.satellite.opacityMax) * this.scaleByDepth(depth, BACKGROUND_CONFIG.satellite.opacityFarScale, BACKGROUND_CONFIG.satellite.opacityNearScale),
      size,
      spacingJitter: spacing * this.randomBetween(BACKGROUND_CONFIG.satellite.spacingJitterMin, BACKGROUND_CONFIG.satellite.spacingJitterMax),
    };
  }

  private getStarlinkDuration(size: number, travelDistance: number) {
    const speedPerSecond = this.getStarlinkSpeedPerSecond(size);

    return (travelDistance / speedPerSecond) * BACKGROUND_CONFIG.math.millisecondsPerSecond;
  }

  private getStarlinkSpeedPerSecond(size: number) {
    const defaultMinStarSize = BACKGROUND_CONFIG.satellite.speedSizeMin;
    const defaultMaxStarSize = BACKGROUND_CONFIG.satellite.speedSizeMax;
    const defaultMinStarSpeedPerFrame = BACKGROUND_CONFIG.satellite.speedPerFrameMin;
    const defaultMaxStarSpeedPerFrame = BACKGROUND_CONFIG.satellite.speedPerFrameMax;
    const satelliteSpeedMultiplier = BACKGROUND_CONFIG.satellite.speedMultiplier;
    const normalizedSize = this.clamp(
      (size - defaultMinStarSize) / (defaultMaxStarSize - defaultMinStarSize),
      BACKGROUND_CONFIG.math.zero,
      BACKGROUND_CONFIG.math.one
    );
    const speedPerFrame = this.lerp(defaultMinStarSpeedPerFrame, defaultMaxStarSpeedPerFrame, normalizedSize) * satelliteSpeedMultiplier;

    return speedPerFrame * BACKGROUND_CONFIG.math.sixty;
  }

  private getStarlinkDotSize(depth: number) {
    const defaultStarAverageSize = BACKGROUND_CONFIG.satellite.defaultStarAverageSize;
    const nearAverageBias = (Math.random() + Math.random() + Math.random()) / BACKGROUND_CONFIG.math.three;
    const closeScale = this.randomBetween(BACKGROUND_CONFIG.satellite.dotCloseScaleMin, BACKGROUND_CONFIG.satellite.dotCloseScaleMax);
    const variedScale = this.randomBetween(BACKGROUND_CONFIG.satellite.dotVariedScaleMin, BACKGROUND_CONFIG.satellite.dotVariedScaleMax);
    const depthScale = this.scaleByDepth(depth, BACKGROUND_CONFIG.satellite.dotDepthFarScale, BACKGROUND_CONFIG.satellite.dotDepthNearScale);

    return defaultStarAverageSize * this.lerp(variedScale, closeScale, nearAverageBias) * depthScale;
  }

  private getStarlinkDotCount() {
    if (Math.random() < BACKGROUND_CONFIG.satellite.easterEggChance) return BACKGROUND_CONFIG.satellite.easterEggDotCount;

    const min = BACKGROUND_CONFIG.satellite.trainDotCountMin;
    const max = BACKGROUND_CONFIG.satellite.trainDotCountMax;
    const rareLongBias = Math.random() ** BACKGROUND_CONFIG.satellite.trainDotLongBiasExponent;
    let shortfall = BACKGROUND_CONFIG.math.zero;

    while (shortfall < BACKGROUND_CONFIG.satellite.trainDotShortfallMax && Math.random() < BACKGROUND_CONFIG.satellite.trainDotShortfallChance) {
      shortfall += BACKGROUND_CONFIG.math.one;
    }

    return Math.max(BACKGROUND_CONFIG.satellite.trainDotAbsoluteMin, Math.round(min + rareLongBias * (max - min)) - shortfall);
  }

  private scaleByDepth(depth: number, farValue: number, nearValue: number) {
    return farValue + depth * (nearValue - farValue);
  }

  private lerp(start: number, end: number, amount: number) {
    return start + (end - start) * amount;
  }

  private clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }

  private getQuadraticPoint(start: Point, control: Point, end: Point, progress: number): Point {
    const inverse = BACKGROUND_CONFIG.math.one - progress;

    return {
      x: inverse ** BACKGROUND_CONFIG.math.two * start.x + BACKGROUND_CONFIG.math.two * inverse * progress * control.x + progress ** BACKGROUND_CONFIG.math.two * end.x,
      y: inverse ** BACKGROUND_CONFIG.math.two * start.y + BACKGROUND_CONFIG.math.two * inverse * progress * control.y + progress ** BACKGROUND_CONFIG.math.two * end.y,
    };
  }

  private getQuadraticTangent(start: Point, control: Point, end: Point, progress: number): Point {
    return {
      x: BACKGROUND_CONFIG.math.two * (BACKGROUND_CONFIG.math.one - progress) * (control.x - start.x) + BACKGROUND_CONFIG.math.two * progress * (end.x - control.x),
      y: BACKGROUND_CONFIG.math.two * (BACKGROUND_CONFIG.math.one - progress) * (control.y - start.y) + BACKGROUND_CONFIG.math.two * progress * (end.y - control.y),
    };
  }

  private getQuadraticCurveLength(start: Point, control: Point, end: Point) {
    const samples = BACKGROUND_CONFIG.curve.lengthSamples;
    let length = BACKGROUND_CONFIG.math.zero;
    let previous = start;

    for (let i = BACKGROUND_CONFIG.math.one; i <= samples; i += BACKGROUND_CONFIG.math.one) {
      const current = this.getQuadraticPoint(start, control, end, i / samples);
      length += Math.hypot(current.x - previous.x, current.y - previous.y);
      previous = current;
    }

    return length;
  }

  private getNormalizedVector(x: number, y: number): Point {
    const length = Math.hypot(x, y);

    if (!length) {
      return { x: BACKGROUND_CONFIG.math.zero, y: BACKGROUND_CONFIG.math.one };
    }

    return {
      x: x / length,
      y: y / length,
    };
  }

  private getMaxPerpendicularOffset(perpendicularX: number, perpendicularY: number) {
    const halfWidth = this.canvasWidth / BACKGROUND_CONFIG.math.two;
    const halfHeight = this.canvasHeight / BACKGROUND_CONFIG.math.two;

    return Math.abs(perpendicularX) * halfWidth + Math.abs(perpendicularY) * halfHeight;
  }

  private randomBetween(min: number, max: number) {
    return min + Math.random() * (max - min);
  }

  private startAnimation() {
    this.animate();
  }

  private animate() {
    this.starlinkNetworkNodes.forEach((node: StarlinkNetworkNode) => {
      node.travel += node.velocity;

      if (node.travel > node.halfTravel) {
        node.travel = -node.halfTravel;
      } else if (node.travel < -node.halfTravel) {
        node.travel = node.halfTravel;
      }

      node.x = node.centerX + node.normalX * node.normalOffset + node.directionX * node.travel;
      node.y = node.centerY + node.normalY * node.normalOffset + node.directionY * node.travel;
      node.trailX = node.x - node.directionX * BACKGROUND_CONFIG.network.trailLength * Math.sign(node.velocity);
      node.trailY = node.y - node.directionY * BACKGROUND_CONFIG.network.trailLength * Math.sign(node.velocity);
    });

    if (this.starlinkNetworkSelection) {
      this.starlinkNetworkSelection
        .attr('cx', (d: StarlinkNetworkNode) => d.x)
        .attr('cy', (d: StarlinkNetworkNode) => d.y);
    }

    if (this.starlinkNetworkTrailSelection) {
      this.starlinkNetworkTrailSelection
        .attr('x1', (d: StarlinkNetworkNode) => d.trailX)
        .attr('y1', (d: StarlinkNetworkNode) => d.trailY)
        .attr('x2', (d: StarlinkNetworkNode) => d.x)
        .attr('y2', (d: StarlinkNetworkNode) => d.y);
    }

    this.stars.forEach((star: Star) => {
      star.x += star.vx;
      if (star.x < BACKGROUND_CONFIG.math.zero) {
        star.x = this.canvasWidth; // Wrap around from left to right
      }
    });

    if (this.starSelection) {
      this.starSelection
        .attr('cx', (d: Star) => d.x)
        .attr('cy', (d: Star) => d.y);
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }
}
